import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Zap, Loader2, Users, Workflow, ShieldCheck, AlertCircle,
} from "lucide-react";

const BENEFITS = [
  { icon: Users, title: "Zentrale Kundenverwaltung", desc: "Alle Kunden, Kontakte und Dokumente an einem Ort." },
  { icon: Workflow, title: "Intelligente Geschäftsprozesse", desc: "Verträge, Fristen und Aufgaben automatisch im Blick." },
  { icon: ShieldCheck, title: "Sicher, flexibel und modular", desc: "Mandantenfähig, DSGVO-orientiert und erweiterbar." },
];

function resolveOrg() {
  const params = new URLSearchParams(window.location.search);
  if (params.get("org")) return params.get("org");
  const host = window.location.hostname;
  const parts = host.split(".");
  // treat a leading subdomain as the org slug (ignore www / plain hosts)
  if (parts.length > 2 && !["www", "app", "preview"].includes(parts[0])) return parts[0];
  return "";
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    const org = resolveOrg();
    api.get(`/public/branding${org ? `?org=${encodeURIComponent(org)}` : ""}`)
      .then(({ data }) => setBrand(data))
      .catch(() => setBrand({ company_name: "primeNetConnect ERP", is_custom: false }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user, data.tenant);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const name = brand?.company_name || "primeNetConnect ERP";
  const isCustom = brand?.is_custom;
  // scope brand colours to this page only (never leaks to other tenants/app)
  const scopeStyle = brand
    ? { "--primary": brand.primary, "--accent": brand.accent }
    : {};

  const LogoMark = ({ className }) => (
    brand?.logo_data
      ? <img src={brand.logo_data} alt={name} className={className + " object-contain"} />
      : <span className={className + " flex items-center justify-center rounded-xl bg-accent text-accent-foreground"}><Zap className="h-5 w-5" /></span>
  );

  return (
    <div
      style={{ ...scopeStyle, minHeight: "100dvh", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      className="flex flex-col bg-background lg:flex-row"
      data-testid="login-page"
    >
      {/* Brand panel — full column on desktop, compact header on mobile */}
      <div className="relative flex shrink-0 flex-col justify-between overflow-hidden bg-[hsl(var(--sidebar))] px-6 py-6 text-[hsl(var(--sidebar-foreground))] lg:w-[46%] lg:px-12 lg:py-12">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />

        <div className="relative flex items-center gap-3">
          <LogoMark className="h-10 w-10 shrink-0" />
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-white">{name}</div>
            {isCustom && <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--sidebar-muted))]">bereitgestellt über primeNetConnect ERP</div>}
          </div>
        </div>

        {/* Headline + benefits (hidden on the smallest screens to keep the form reachable) */}
        <div className="relative mt-8 hidden max-w-md sm:block lg:mt-0">
          <h1 className="text-3xl font-semibold leading-tight text-white text-balance lg:text-4xl" data-testid="login-headline">
            Alles im Griff. Ein System.
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-[hsl(var(--sidebar-muted))]">
            Verwalten Sie Kunden, Verträge, Dokumente und Geschäftsprozesse zentral an einem Ort.
          </p>
          <div className="mt-8 space-y-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-accent ring-1 ring-white/10">
                  <b.icon className="h-[18px] w-[18px]" />
                </span>
                <div>
                  <div className="text-sm font-medium text-white">{b.title}</div>
                  <div className="text-xs text-[hsl(var(--sidebar-muted))]">{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compact tagline for the smallest viewports */}
        <p className="relative mt-3 text-sm text-[hsl(var(--sidebar-muted))] sm:hidden">
          Kunden, Verträge und Prozesse — zentral an einem Ort.
        </p>

        <div className="relative mt-8 hidden text-[11px] text-[hsl(var(--sidebar-muted))] lg:block">
          © 2026 primeNetConnect ERP
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-1 items-center justify-center px-5 py-10 sm:px-8">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-semibold tracking-tight">Willkommen zurück</h2>
          <p className="mt-1 text-sm text-muted-foreground">Melden Sie sich bei Ihrem Arbeitsbereich an.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {error && (
              <Alert variant="destructive" data-testid="login-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">E-Mail</Label>
              <Input id="email" type="email" inputMode="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11" data-testid="login-email-input" placeholder="name@firma.de" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Passwort</Label>
              <Input id="password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-11" data-testid="login-password-input" placeholder="••••••••" />
            </div>
            <Button type="submit" className="h-11 w-full bg-accent text-base text-accent-foreground hover:bg-accent/90"
              disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Anmelden
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> oder <div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" className="h-11 w-full" disabled title="Nicht konfiguriert" data-testid="ms365-login-button">
            Mit Microsoft 365 anmelden
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">Nicht konfiguriert</span>
          </Button>
          <p className="mt-6 text-center text-[11px] text-muted-foreground">
            Microsoft 365 wird aktiv, sobald Ihre Entra-ID-Zugangsdaten hinterlegt sind.
          </p>
        </div>
      </div>
    </div>
  );
}
